import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayoutWithoutFooter from '../components/MainLayoutWithoutFooter';
import TeamTimesheet from '../components/TeamTimesheet';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import '../styles/ValidatePinModal.css';

const PIN_LENGTH = 4;

/**
 * Manager/admin "Team Timesheets" page (/team-timesheets) - all staff shifts, filters and Excel
 * export. Separate from the personal /clockin-clockout page, which stays as-is. Gated on a PIN
 * whose owner has `staff.view` (TimesheetController::TEAM_PERMISSION); validate-pin then hands
 * back the team_token the team-* endpoints authenticate with.
 */
const TeamTimesheets = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [digits, setDigits] = useState(Array(PIN_LENGTH).fill(''));
  // Arriving from the personal Timesheet's "View all staff timesheets" button: that PIN was
  // already validated there, so skip asking again. The server still re-checks the token.
  const [employee, setEmployee] = useState(() => {
    const passed = location.state?.employee;
    return passed?.can_view_team && passed?.team_token ? passed : null;
  });
  const [validating, setValidating] = useState(false);
  const inputRefs = useRef([]);

  const resetPinEntry = () => {
    setDigits(Array(PIN_LENGTH).fill(''));
    setTimeout(() => inputRefs.current[0]?.focus(), 50);
  };

  const submitPin = async (pin) => {
    setValidating(true);
    try {
      const res = await callAPI(apiUrls.TIMESHEET_VALIDATE_PIN, {}, 'POST', { pin });
      const data = res.data?.data;
      if (!data) {
        toast.error('Invalid PIN');
        resetPinEntry();
      } else if (!data.can_view_team || !data.team_token) {
        toast.error('Only managers and admins can view team timesheets.');
        resetPinEntry();
      } else {
        setEmployee(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.errors?.pin?.[0] || 'Invalid PIN');
      resetPinEntry();
    } finally {
      setValidating(false);
    }
  };

  const handleChange = (index, rawValue) => {
    const value = rawValue.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = value;
    setDigits(next);

    if (value && index < PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    if (value && index === PIN_LENGTH - 1) {
      submitPin(next.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      submitPin(digits.join(''));
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(PIN_LENGTH).fill('');
    pasted.split('').forEach((char, i) => { next[i] = char; });
    setDigits(next);
    if (pasted.length === PIN_LENGTH) {
      submitPin(pasted);
    } else {
      inputRefs.current[pasted.length]?.focus();
    }
  };

  if (employee) {
    return <TeamTimesheet employee={employee} />;
  }

  return (
    <MainLayoutWithoutFooter>
      <div className="pageContent timesheetsPage pb-3">
        <div className="container d-flex justify-content-center">
          <div className="pinModalBody" style={{ maxWidth: 380, width: '100%' }}>
            <div className="pinModalIcon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M17 20h5v-2a3 3 0 00-5.36-1.86M17 20H7m10 0v-2c0-.66-.13-1.28-.36-1.86M7 20H2v-2a3 3 0 015.36-1.86M7 20v-2c0-.66.13-1.28.36-1.86m0 0a5 5 0 019.28 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1 className="pinModalTitle">Team Timesheets</h1>
            <p className="pinModalSubtitle">Manager or admin PIN required</p>

            <div className="pinDigitsRow" onPaste={handlePaste}>
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  className="pinDigitBox"
                  value={digit}
                  disabled={validating}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <button
              className="pinValidateBtn"
              onClick={() => submitPin(digits.join(''))}
              disabled={validating}
            >
              {validating ? 'Checking...' : 'Continue'}
            </button>
            <button
              type="button"
              className="underlineLink mt-3"
              onClick={() => navigate('/clockin-clockout')}
            >
              Back to my timesheet
            </button>
          </div>
        </div>
      </div>
    </MainLayoutWithoutFooter>
  );
};

export default TeamTimesheets;
